import { useAppDispatch } from '@/lib/redux/store';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectOpenModals } from './redux/modals.selectors';
import { removeOpenModal } from './redux/modals.slice';

interface UseModalsProps {
  modalName?: string;
  modalParams?: unknown;
  shouldCloseOnEscape?: boolean;
}
const useModals = ({
  modalName,
  modalParams,
  shouldCloseOnEscape = false,
}: UseModalsProps) => {
  const dispatch = useAppDispatch();
  const openModals = useSelector(selectOpenModals);
  const isModalOpen = useMemo(
    () => !!openModals.find((m) => m.modalName === modalName),
    [modalName, openModals]
  );
  const router = useRouter();

  const searchParams = useSearchParams();
  const pathname = usePathname();

  const urlWithModalParams = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    if (modalName) {
      params.set('modal', modalName);
    }
    if (modalParams) {
      params.set('modal-params', JSON.stringify(modalParams));
    }
    return `${pathname}?${params.toString()}`;
  }, [modalName, modalParams, pathname, searchParams]);

  // URL without Modal params - to close the Modal
  const closeModalUrl = useMemo(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('modal');
    params.delete('modal-params');

    // Also delete individual keys from modalParams if they were set as top-level params
    if (modalParams && typeof modalParams === 'object') {
      Object.keys(modalParams).forEach((key) => {
        params.delete(key);
      });
    }

    const paramsString = params.toString() ? `?${params.toString()}` : '';
    return `${pathname}${paramsString}`;
  }, [pathname, searchParams, modalParams]);

  const onClose = () => {
    onCloseUrl();
    onCloseState();
  };

  const onCloseUrl = () => {
    // Always replace the URL to remove modal params when closing.
    // scroll: false preserves the scroll position so the page doesn't jump to top
    // after adding/editing a sub-form (e.g. tracked link, question, reply template).
    router.replace(closeModalUrl, { scroll: false });
  };
  const onCloseState = () => {
    if (modalName) {
      dispatch(removeOpenModal(modalName));
    }
  };

  return {
    urlWithModalParams,
    closeModalUrl,
    isModalOpen,
    onCloseUrl,
    onCloseState,
    onClose,
    shouldCloseOnEscape,
  };
};

export default useModals;
